// The returns desk's words in the worker's own language (Fernando 22.10: the desk is the physical side — scan, count,
// put the piece where the screen says; no money, no exchange, no "why"). The language is the person's preference
// (users.lang, GET /api/returns/desk/who). ET and RU were written by Claude and are to be checked by Fernando.

export type RLang = 'en' | 'et' | 'ru';

export interface ReturnsTexts {
  title: string;
  scanPrompt: string;            // the big box, waiting for a parcel
  scanItems: string;             // the box while an order is open
  noLabel: string;
  noLabelTitle: string;
  noLabelHelp: string;
  noLabelNone: string;
  allItems: string;
  notFound: (code: string) => string;
  partialLabel: string;
  whichOrder: string;
  order: string;
  confirm: string;
  cancel: string;
  undo: string;
  nothingScanned: string;
  notOnOrder: string;            // a scan that is not one of the order's pieces
  lineFull: string;              // more of a line than can come back
  alreadyBack: string;           // a line that already came back on an earlier parcel
  typeCount: string;
  putHere: string;
  places: { polishing: string; defects: string };
  nextParcel: string;
  repeat: string;
  confirmFailed: string;
  accepted: (n: number) => string;
  sendToWms: (n: number) => string;
  selectAll: string;
  deselectAll: string;
  noneAccepted: string;
  colOrder: string;
  colStore: string;
  colPieces: string;
  colBy: string;
  removeAsk: string;
  created: (code: string, lines: number) => string;
  working: (name: string) => string;
  signOut: string;
  whoTitle: string;
  badgeOrPin: string;            // 1.11: under every identify title
  cancelBadge: string;
  // polishing (Phase B, 28.10)
  startScanning: string;
  saveScanning: string;
  scanPolished: string;
  polishingHelp: string;
  counted: (what: string, n: number, of: number) => string;
  notOnReturnOrder: (code: string) => string;
  overScan: (what: string, of: number) => string;
  whichParcel: (code: string) => string;
  defectButton: (what: string) => string;
  defectsBox: (what: string) => string;
  inDefectsBox: (n: number) => string;
  undoDefect: string;
  saved: (n: number) => string;
}

const T: Record<RLang, ReturnsTexts> = {
  en: {
    title: 'Accept returns',
    scanPrompt: 'Scan the delivery note or the shipping label',
    scanItems: 'Scan each piece in the parcel',
    noLabel: 'No label?',
    noLabelTitle: 'Find the order by its pieces',
    noLabelHelp: 'Scan every piece in the parcel. Then tap the right order.',
    noLabelNone: 'No order has these pieces.',
    allItems: 'all pieces',
    notFound: code => `Nothing matched "${code}"`,
    partialLabel: 'Found from part of a shipping label — check it is the right order.',
    whichOrder: 'Which order is this parcel from?',
    order: 'Order',
    confirm: 'Confirm',
    cancel: 'Cancel',
    undo: 'Undo last scan',
    nothingScanned: 'Scan the pieces first',
    notOnOrder: 'Not on this order — put it aside',
    lineFull: 'All pieces of this item are counted already',
    alreadyBack: 'came back already',
    typeCount: 'How many?',
    putHere: 'Put the pieces here',
    places: { polishing: 'Polishing', defects: 'Defects box' },
    nextParcel: 'Scan the next parcel',
    repeat: 'Already saved',
    confirmFailed: 'Could not save — try again',
    accepted: n => `Accepted, not sent yet (${n})`,
    sendToWms: n => `Send to WMS (${n})`,
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    noneAccepted: 'Nothing accepted yet.',
    colOrder: 'Order',
    colStore: 'Store',
    colPieces: 'Pieces',
    colBy: 'By',
    removeAsk: 'Remove this parcel from the list?',
    created: (code, lines) => `Return order ${code} created (${lines} lines)`,
    working: name => `Working: ${name}`,
    signOut: 'Sign out',
    whoTitle: 'Who is working?',
    badgeOrPin: 'Scan your badge or enter your PIN',
    cancelBadge: 'Cancel',
    startScanning: 'Start polishing',
    saveScanning: 'Save polishing',
    scanPolished: 'Scan each piece as you polish it',
    polishingHelp: 'A faulty piece: press Defect (or scan the DEFECT card) right after scanning it.',
    counted: (what, n, of) => `${what} · ${n} / ${of}`,
    notOnReturnOrder: code => `${code} is not on this return order — put it aside`,
    overScan: (what, of) => `${what}: all ${of} are scanned already`,
    whichParcel: code => `${code} is on more than one line — which parcel did it come from?`,
    defectButton: what => `Defect — ${what}`,
    defectsBox: what => `${what} → DEFECTS BOX`,
    inDefectsBox: n => `${n} in the defects box`,
    undoDefect: 'not a defect',
    saved: n => `Saved: ${n} polished`,
  },
  et: {
    title: 'Tagastuste vastuvõtt',
    scanPrompt: 'Skanni saateleht või pakikaart',
    scanItems: 'Skanni pakis iga ese',
    noLabel: 'Silti pole?',
    noLabelTitle: 'Leia tellimus esemete järgi',
    noLabelHelp: 'Skanni pakis kõik esemed. Siis vajuta õigele tellimusele.',
    noLabelNone: 'Nende esemetega tellimust ei leitud.',
    allItems: 'kõik esemed',
    notFound: code => `„${code}“ ei leitud`,
    partialLabel: 'Leitud pakikaardi osa järgi — kontrolli, et tellimus on õige.',
    whichOrder: 'Millisest tellimusest see pakk on?',
    order: 'Tellimus',
    confirm: 'Kinnita',
    cancel: 'Tühista',
    undo: 'Võta viimane skänn tagasi',
    nothingScanned: 'Skanni kõigepealt esemed',
    notOnOrder: 'Ei ole selles tellimuses — pane kõrvale',
    lineFull: 'Selle eseme kõik tükid on juba loetud',
    alreadyBack: 'juba tagastatud',
    typeCount: 'Mitu?',
    putHere: 'Pane esemed siia',
    places: { polishing: 'Poleerimine', defects: 'Defektide kast' },
    nextParcel: 'Skanni järgmine pakk',
    repeat: 'Juba salvestatud',
    confirmFailed: 'Salvestamine ei õnnestunud — proovi uuesti',
    accepted: n => `Vastu võetud, saatmata (${n})`,
    sendToWms: n => `Saada WMS-i (${n})`,
    selectAll: 'Vali kõik',
    deselectAll: 'Tühista valik',
    noneAccepted: 'Veel pole midagi vastu võetud.',
    colOrder: 'Tellimus',
    colStore: 'Pood',
    colPieces: 'Tükke',
    colBy: 'Kes',
    removeAsk: 'Eemaldada see pakk nimekirjast?',
    created: (code, lines) => `Tagastustellimus ${code} loodud (${lines} rida)`,
    working: name => `Töötab: ${name}`,
    signOut: 'Logi välja',
    whoTitle: 'Kes töötab?',
    badgeOrPin: 'Skanni oma kaart või sisesta PIN',
    cancelBadge: 'Tühista',
    startScanning: 'Alusta poleerimist',
    saveScanning: 'Salvesta poleerimine',
    scanPolished: 'Skanni iga ese, kui oled selle poleerinud',
    polishingHelp: 'Vigane ese: vajuta kohe pärast skannimist „Defekt“ (või skanni DEFECT-kaart).',
    counted: (what, n, of) => `${what} · ${n} / ${of}`,
    notOnReturnOrder: code => `${code} ei ole selles tagastustellimuses — pane kõrvale`,
    overScan: (what, of) => `${what}: kõik ${of} on juba skannitud`,
    whichParcel: code => `${code} on mitmel real — millisest pakist see tuli?`,
    defectButton: what => `Defekt — ${what}`,
    defectsBox: what => `${what} → DEFEKTIDE KAST`,
    inDefectsBox: n => `${n} defektide kastis`,
    undoDefect: 'pole defekt',
    saved: n => `Salvestatud: ${n} poleeritud`,
  },
  ru: {
    title: 'Приём возвратов',
    scanPrompt: 'Отсканируйте накладную или этикетку посылки',
    scanItems: 'Отсканируйте каждую вещь в посылке',
    noLabel: 'Нет этикетки?',
    noLabelTitle: 'Найти заказ по вещам',
    noLabelHelp: 'Отсканируйте все вещи в посылке. Затем нажмите на нужный заказ.',
    noLabelNone: 'Заказа с этими вещами нет.',
    allItems: 'все вещи',
    notFound: code => `«${code}» не найдено`,
    partialLabel: 'Найдено по части этикетки — проверьте, что заказ правильный.',
    whichOrder: 'Из какого заказа эта посылка?',
    order: 'Заказ',
    confirm: 'Подтвердить',
    cancel: 'Отмена',
    undo: 'Отменить последний скан',
    nothingScanned: 'Сначала отсканируйте вещи',
    notOnOrder: 'Нет в этом заказе — отложите в сторону',
    lineFull: 'Все штуки этой вещи уже посчитаны',
    alreadyBack: 'уже вернули',
    typeCount: 'Сколько?',
    putHere: 'Положите вещи сюда',
    places: { polishing: 'Полировка', defects: 'Ящик для брака' },
    nextParcel: 'Отсканируйте следующую посылку',
    repeat: 'Уже сохранено',
    confirmFailed: 'Не удалось сохранить — попробуйте ещё раз',
    accepted: n => `Принято, ещё не отправлено (${n})`,
    sendToWms: n => `Отправить в WMS (${n})`,
    selectAll: 'Выбрать все',
    deselectAll: 'Снять выбор',
    noneAccepted: 'Пока ничего не принято.',
    colOrder: 'Заказ',
    colStore: 'Магазин',
    colPieces: 'Штук',
    colBy: 'Кто',
    removeAsk: 'Убрать эту посылку из списка?',
    created: (code, lines) => `Заказ возврата ${code} создан (${lines} строк)`,
    working: name => `Работает: ${name}`,
    signOut: 'Выйти',
    whoTitle: 'Кто работает?',
    badgeOrPin: 'Отсканируйте бейдж или введите PIN',
    cancelBadge: 'Отмена',
    startScanning: 'Начать полировку',
    saveScanning: 'Сохранить полировку',
    scanPolished: 'Сканируйте каждую вещь после полировки',
    polishingHelp: 'Бракованная вещь: сразу после скана нажмите «Брак» (или отсканируйте карточку DEFECT).',
    counted: (what, n, of) => `${what} · ${n} / ${of}`,
    notOnReturnOrder: code => `${code} нет в этом заказе возврата — отложите в сторону`,
    overScan: (what, of) => `${what}: все ${of} уже отсканированы`,
    whichParcel: code => `${code} есть в нескольких строках — из какой посылки эта вещь?`,
    defectButton: what => `Брак — ${what}`,
    defectsBox: what => `${what} → ЯЩИК ДЛЯ БРАКА`,
    inDefectsBox: n => `${n} в ящике для брака`,
    undoDefect: 'не брак',
    saved: n => `Сохранено: ${n} отполировано`,
  },
};

export function returnsText(lang?: string | null): ReturnsTexts {
  return T[(lang as RLang)] || T.en;
}
